/**
 * Sport Configuration
 *
 * Multi-sport support: Track & Field (default), Swimming, Cross Country.
 * Each sport has its own UI vocabulary, default event blocks, baseline
 * durations, and feature flags (e.g. show pool lanes, hide field events).
 *
 * NOTE: data persistence still flows through the existing MongoDB `meets`
 * collection — we just stamp `sport_type` / `pool_lanes` / `swim_preset`
 * onto every meet. There is no separate `event_templates` table; defaults
 * live in this JS module.
 *
 * EVENT TEMPLATE CONTRACT (Feb 2026 update)
 * -----------------------------------------
 * Every preset event MUST expose BOTH:
 *   • `default_heats`              — integer ≥ 1
 *   • `default_duration_seconds`   — integer seconds (≥ 30)
 *
 * The schedule prediction engine needs these as primary baselines.
 * `duration` (mm:ss string) is also exposed for the existing
 * `eventConfig.js` consumers; it's derived from
 * `default_duration_seconds` so the two never disagree.
 */

export const SPORT_TYPES = ['track', 'swim', 'xc'];
export const DEFAULT_SPORT_TYPE = 'track';

// Pool lane options for swim meets (heats are typically sized to fit lanes).
// 4-lane pools exist in small summer-club venues; 6/8 are most common; 10
// is championship / collegiate.
export const POOL_LANES_OPTIONS = [4, 6, 8, 10];
export const DEFAULT_POOL_LANES = 8;

// Pool measurement unit — flips event names between Short Course Yards
// (SCY, 'y' suffix) and Meters ('m' suffix) AND swaps the NFHS 500 free
// for a 400 free when meters is selected.
export const POOL_UNITS = ['yards', 'meters'];
export const DEFAULT_POOL_UNIT = 'yards';

// Swim event presets — coaches pick one in the wizard.
export const SWIM_PRESETS = ['standard_hs', 'summer_club'];
export const DEFAULT_SWIM_PRESET = 'standard_hs';

// Bulk-add quick path: when a coach taps "Add Custom Event" we seed
// 60 seconds per heat. Summer-club coaches use this for the long tail
// of age-group sprint events the canned preset doesn't cover.
export const CUSTOM_EVENT_DEFAULT_DURATION_SECONDS = 60;
export const CUSTOM_EVENT_DEFAULT_HEATS = 1;

// ---------------------------------------------------------------------------
// Settings-page baselines (Feb 2026).
//
// Each row models an editable default that the schedule engine consults
// when seeding a new meet. Stored flat in `localStorage.foresight-settings`
// under the documented `key`. The dual `*_duration_seconds` / `*_heats`
// fields satisfy the granular-pacing contract — the schedule engine
// reads the seconds field directly.
//
// IMPORTANT: keys here must be unique across sports so the flat
// localStorage namespace doesn't collide. Existing Track keys are
// `timeBetweenEventsIndoor` / `timeBetweenEventsOutdoor` plus the
// per-event `<eventId>_heats_<level>` / `<eventId>_duration_<level>`
// pairs that `settings.js` already manages — those are NOT redefined
// here so the legacy Track save/load flow is untouched.
// ---------------------------------------------------------------------------

export const SWIM_SENIOR_BASELINES = [
  { id: 'swim_senior_50_sprint',    label: '50 Sprints',         default_duration_seconds: 60,  default_heats: 4 },
  { id: 'swim_senior_100',          label: '100 Events',         default_duration_seconds: 120, default_heats: 3 },
  { id: 'swim_senior_200',          label: '200 Events',         default_duration_seconds: 210, default_heats: 2 },
  { id: 'swim_senior_500_distance', label: '500 / 400 Distance', default_duration_seconds: 450, default_heats: 1 },
  { id: 'swim_senior_200_relay',    label: '200 Relays',         default_duration_seconds: 180, default_heats: 2 },
];

export const SWIM_YOUTH_BASELINES = [
  { id: 'swim_youth_25_sprint', label: '25 Sprints',       default_duration_seconds: 90,  default_heats: 5 },
  { id: 'swim_youth_50_sprint', label: '50 Youth Sprints', default_duration_seconds: 105, default_heats: 4 },
  { id: 'swim_youth_100',       label: '100 Youth Events', default_duration_seconds: 180, default_heats: 3 },
  { id: 'swim_youth_relay',     label: 'Youth Relays',     default_duration_seconds: 210, default_heats: 2 },
];

export const XC_BASELINES = [
  { id: 'xc_varsity_5k_boys',  label: 'Varsity 5k (Boys / Mixed)', default_duration_seconds: 1800, default_heats: 1 },
  { id: 'xc_varsity_5k_girls', label: 'Varsity 5k (Girls)',        default_duration_seconds: 2100, default_heats: 1 },
  { id: 'xc_jv_open_5k',       label: 'JV / Open 5k',              default_duration_seconds: 2400, default_heats: 1 },
  { id: 'xc_ms_2mile',         label: 'Middle School 2-Mile',      default_duration_seconds: 1500, default_heats: 1 },
];

// Per-sport "time between events" keys. Track keeps its legacy
// indoor/outdoor split so we don't migrate existing localStorage.
export const TRANSITION_KEYS = {
  track_indoor: 'timeBetweenEventsIndoor',
  track_outdoor: 'timeBetweenEventsOutdoor',
  swim: 'timeBetweenEvents_swim',
  xc: 'timeBetweenEvents_xc',
};

// Default sport profile dropdown on the global Settings page.
export const DEFAULT_SPORT_PROFILE_KEY = 'defaultSportProfile';

/**
 * Build the flat default-settings dictionary contribution for the
 * Settings-page baselines. Spread into `DEFAULT_SETTINGS` in
 * `settings.js` so a brand-new install boots with sensible values.
 */
export function buildSettingsBaselineDefaults() {
  const out = {
    [DEFAULT_SPORT_PROFILE_KEY]: DEFAULT_SPORT_TYPE,
    [TRANSITION_KEYS.swim]: '0',
    [TRANSITION_KEYS.xc]: '5', // XC race-to-race gap is typically 5min+
  };
  [...SWIM_SENIOR_BASELINES, ...SWIM_YOUTH_BASELINES, ...XC_BASELINES].forEach((row) => {
    out[`${row.id}_duration`] = row.default_duration_seconds;
    out[`${row.id}_heats`] = row.default_heats;
  });
  return out;
}

// Vocabulary mapping. Track and Swim share Event/Heat terminology; XC uses
// Division/Race. The plural forms are used in summary strings ("3 events"
// vs "3 divisions" etc).
const VOCAB = {
  track: { event: 'Event', heat: 'Heat', events: 'events', heats: 'heats' },
  swim: { event: 'Event', heat: 'Heat', events: 'events', heats: 'heats' },
  xc: { event: 'Division', heat: 'Race', events: 'divisions', heats: 'races' },
};

const SPORT_LABELS = {
  track: 'Track & Field',
  swim: 'Swimming',
  xc: 'Cross Country',
};

const SPORT_FEATURES = {
  track: { showFieldEvents: true, showPoolLanes: false },
  swim: { showFieldEvents: false, showPoolLanes: true },
  xc: { showFieldEvents: false, showPoolLanes: false },
};

const SWIM_PRESET_LABELS = {
  standard_hs: 'Standard High School',
  summer_club: 'Summer Swim Club',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function normalizeSportType(value) {
  if (!value) return DEFAULT_SPORT_TYPE;
  const v = String(value).toLowerCase();
  return SPORT_TYPES.includes(v) ? v : DEFAULT_SPORT_TYPE;
}

export function normalizeSwimPreset(value) {
  if (!value) return DEFAULT_SWIM_PRESET;
  return SWIM_PRESETS.includes(value) ? value : DEFAULT_SWIM_PRESET;
}

/**
 * Normalize a meet's pool_unit field. Older meets in MongoDB that
 * pre-date this field default to 'yards' (SCY) — the original
 * hardcoded behavior — so the spectator view never renders blank
 * event names.
 */
export function normalizePoolUnit(value) {
  if (!value) return DEFAULT_POOL_UNIT;
  const v = String(value).toLowerCase();
  return POOL_UNITS.includes(v) ? v : DEFAULT_POOL_UNIT;
}

// Single-character distance suffix appended to numeric distances in
// generated event names ('25y Freestyle' vs '25m Freestyle').
export function getDistanceSuffix(poolUnit) {
  return normalizePoolUnit(poolUnit) === 'meters' ? 'm' : 'y';
}

export function getSportLabel(sportType) {
  return SPORT_LABELS[normalizeSportType(sportType)];
}

export function getSportVocab(sportType) {
  return VOCAB[normalizeSportType(sportType)];
}

export function getSportFeatures(sportType) {
  return SPORT_FEATURES[normalizeSportType(sportType)];
}

export function shouldShowFieldEvents(sportType) {
  return getSportFeatures(sportType).showFieldEvents;
}

export function shouldShowPoolLanes(sportType) {
  return getSportFeatures(sportType).showPoolLanes;
}

export function getSwimPresetLabel(preset) {
  return SWIM_PRESET_LABELS[normalizeSwimPreset(preset)];
}

// Convert an integer second count to the canonical 'mm:ss' string the
// rest of the app uses for `duration` fields. Caller is responsible for
// keeping seconds non-negative.
export function secondsToMMSS(seconds) {
  const total = Math.max(0, Math.round(seconds));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

// Inverse — parse 'mm:ss' to seconds. Returns null for malformed input
// so callers can fall back to a sport default.
export function mmssToSeconds(str) {
  if (typeof str !== 'string') return null;
  const match = str.match(/^(\d+):(\d{1,2})$/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

// Build a normalized template object from a sparse spec. Ensures every
// returned template carries `default_heats` + `default_duration_seconds`
// alongside the legacy `defaultHeats` + `duration` keys ManualEventEntry
// already reads.
function buildTemplate(sport, level, { name, type, heats, durationSeconds, ageGroup }) {
  const safeHeats = Math.max(1, Math.round(heats || CUSTOM_EVENT_DEFAULT_HEATS));
  const safeSeconds = Math.max(30, Math.round(durationSeconds || CUSTOM_EVENT_DEFAULT_DURATION_SECONDS));
  return {
    name,
    type,
    eventId: `${sport}_${level}_${name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
    // New explicit baseline fields (Feb 2026):
    default_heats: safeHeats,
    default_duration_seconds: safeSeconds,
    // Legacy fields, derived so the schedule engine + EventCard
    // continue to work without per-call conversion. Never set these
    // independently — always pass through this builder.
    defaultHeats: safeHeats,
    duration: secondsToMMSS(safeSeconds),
    // Optional free-form age-group tag (summer-club presets use this
    // heavily). Surfaced through to the event card as the editable
    // `ageGroup` field.
    ageGroup: ageGroup || null,
  };
}

// ---------------------------------------------------------------------------
// SWIM presets
// ---------------------------------------------------------------------------
//
// "Standard High School" — strict NFHS event order for HS dual meets.
// Event #7 swaps "500 Freestyle" for "400 Freestyle" when the meet is
// run in meters (per NFHS rule book — the distance-free converts to
// 400m on a metric pool).
// "Summer Swim Club" — age-group meets that fan out across 6&Under,
// 7-8, 9-10, 11-12, 13-14, 15-18 brackets. Stroke-major ordering
// (Medley Relays → short free → back → breast → fly → long free →
// freestyle relays) — same chronological pattern used by USA Swimming
// age-group meets.
//
// All distance numbers are unit-agnostic at the data layer; the
// distance suffix ('y' or 'm') is appended at build time based on the
// meet's pool_unit field.
// ---------------------------------------------------------------------------

/**
 * NFHS HIGH SCHOOL event order (Feb 2026).
 * Per NFHS Swimming & Diving Rules Book, Rule 3, Event Order.
 * Returns an 11-event list in strict order, with the
 * distance-freestyle swapped to 400 in metric pools.
 */
function buildStandardHSSwimEvents(unit) {
  const u = getDistanceSuffix(unit);
  const distanceFree = unit === 'meters'
    ? { distance: 400, durationSeconds: 300 } // 400m takes ~5min target
    : { distance: 500, durationSeconds: 390 }; // 500y standard
  return [
    { name: `200${u} Medley Relay`,      type: 'relay',    heats: 2, durationSeconds: 150 },
    { name: `200${u} Freestyle`,         type: 'distance', heats: 3, durationSeconds: 150 },
    { name: `200${u} IM`,                type: 'distance', heats: 2, durationSeconds: 180 },
    { name: `50${u} Freestyle`,          type: 'sprint',   heats: 4, durationSeconds: 60  },
    { name: `100${u} Butterfly`,         type: 'sprint',   heats: 2, durationSeconds: 90  },
    { name: `100${u} Freestyle`,         type: 'sprint',   heats: 4, durationSeconds: 90  },
    { name: `${distanceFree.distance}${u} Freestyle`,
                                          type: 'distance', heats: 2, durationSeconds: distanceFree.durationSeconds },
    { name: `200${u} Freestyle Relay`,   type: 'relay',    heats: 2, durationSeconds: 120 },
    { name: `100${u} Backstroke`,        type: 'sprint',   heats: 2, durationSeconds: 90  },
    { name: `100${u} Breaststroke`,      type: 'sprint',   heats: 2, durationSeconds: 90  },
    { name: `400${u} Freestyle Relay`,   type: 'relay',    heats: 2, durationSeconds: 240 },
  ];
}

function buildMiddleSchoolSwimEvents(unit) {
  const u = getDistanceSuffix(unit);
  return [
    { name: `200${u} Medley Relay`,    type: 'relay',    heats: 2, durationSeconds: 180 },
    { name: `100${u} Freestyle`,       type: 'sprint',   heats: 3, durationSeconds: 90  },
    { name: `50${u} Backstroke`,       type: 'sprint',   heats: 2, durationSeconds: 60  },
    { name: `50${u} Breaststroke`,     type: 'sprint',   heats: 2, durationSeconds: 60  },
    { name: `50${u} Butterfly`,        type: 'sprint',   heats: 2, durationSeconds: 60  },
    { name: `50${u} Freestyle`,        type: 'sprint',   heats: 3, durationSeconds: 60  },
    { name: `100${u} IM`,              type: 'distance', heats: 2, durationSeconds: 120 },
    { name: `200${u} Freestyle Relay`, type: 'relay',    heats: 2, durationSeconds: 150 },
  ];
}

function buildCollegeSwimEvents(unit) {
  const u = getDistanceSuffix(unit);
  // College / collegiate dual — distance-free is 500y / 400m as with HS.
  const distanceFree = unit === 'meters'
    ? { distance: 400, durationSeconds: 270 }
    : { distance: 500, durationSeconds: 330 };
  return [
    { name: `200${u} Medley Relay`,    type: 'relay',    heats: 2, durationSeconds: 120 },
    { name: `${distanceFree.distance}${u} Freestyle`,
                                        type: 'distance', heats: 2, durationSeconds: distanceFree.durationSeconds },
    { name: `200${u} IM`,              type: 'distance', heats: 2, durationSeconds: 150 },
    { name: `50${u} Freestyle`,        type: 'sprint',   heats: 3, durationSeconds: 60  },
    { name: `100${u} Butterfly`,       type: 'sprint',   heats: 2, durationSeconds: 90  },
    { name: `100${u} Backstroke`,      type: 'sprint',   heats: 2, durationSeconds: 90  },
    { name: `100${u} Breaststroke`,    type: 'sprint',   heats: 2, durationSeconds: 90  },
    { name: `200${u} Freestyle Relay`, type: 'relay',    heats: 2, durationSeconds: 120 },
    { name: `400${u} Freestyle Relay`, type: 'relay',    heats: 2, durationSeconds: 210 },
  ];
}

/**
 * Summer Swim Club generator (Feb 2026, stroke-major order).
 *
 * The chronological flow follows real-world age-group meets:
 *   1. Medley Relays         — opening event(s)
 *   2. Short Freestyle       — 25 for young, 50 for older
 *   3. Backstroke            — 25 young / 50 older across all brackets
 *   4. Breaststroke          — 25 young / 50 older across all brackets
 *   5. Butterfly             — 25 young / 50 older across all brackets
 *   6. Individual Medley     — 100 young / 200 older across all brackets
 *   7. Long Freestyle        — 50 young, 100 older
 *   8. Freestyle Relays      — closing event(s)
 *
 * For each stroke section we iterate through every age bracket
 * (6&U → 15-18) so the timer/announcer can run all 6&U races back to
 * back before moving up. Distances flip from "young" (25 short / 50
 * long) to "older" (50 short / 100 long) at the 11-12 bracket per
 * USA Swimming convention. IM follows the same flip point (100 young
 * / 200 older) and stays aligned with the SWIM_YOUTH_100 baseline
 * for the young rows (180s × 3-heat catalog default) so the Distance
 * dropdown recompute in ManualEventEntry lands on identical values
 * when a coach later scales an IM row up or down.
 */
function buildSummerClubEvents(unit) {
  const u = getDistanceSuffix(unit);
  const out = [];

  // Age brackets — keep order identical to summer-meet announcer flow.
  // `young` flips the short/long freestyle + per-stroke distances at
  // 11-12.
  const ageGroups = [
    { label: '6 & Under', young: true },
    { label: '7-8',       young: true },
    { label: '9-10',      young: true },
    { label: '11-12',     young: false },
    { label: '13-14',     young: false },
    { label: '15-18',     young: false },
  ];

  // 1. Medley Relays — 100 for young, 200 for older.
  ageGroups.forEach((ag) => {
    out.push({
      name: `${ag.young ? 100 : 200}${u} Medley Relay`,
      type: 'relay',
      heats: 1,
      durationSeconds: ag.young ? 180 : 240,
      ageGroup: ag.label,
    });
  });

  // 2. Short Freestyle — 25 young / 50 older.
  ageGroups.forEach((ag) => {
    out.push({
      name: `${ag.young ? 25 : 50}${u} Freestyle`,
      type: 'sprint',
      heats: ag.young ? 2 : 3,
      durationSeconds: ag.young ? 90 : 110,
      ageGroup: ag.label,
    });
  });

  // 3-5. Backstroke / Breaststroke / Butterfly — 25 young / 50 older.
  ['Backstroke', 'Breaststroke', 'Butterfly'].forEach((stroke) => {
    ageGroups.forEach((ag) => {
      out.push({
        name: `${ag.young ? 25 : 50}${u} ${stroke}`,
        type: 'sprint',
        heats: 2,
        durationSeconds: ag.young ? 90 : 110,
        ageGroup: ag.label,
      });
    });
  });

  // 6. Individual Medley — 100y for 6&U through 11-12, 200y for
  // 13-14 and 15-18. The IM distance flip lives at 13-14 per USA
  // Swimming age-group convention (11-12 swimmers race the 100 IM
  // in club meets), which is one bracket LATER than the shared
  // `young` flag's flip point at 11-12 — hence the dedicated
  // `isImYoung` check below instead of reusing `ag.young`.
  // heats + durations mirror the SWIM_YOUTH_100 baseline
  // (180s × 3-heat catalog default) for the 100y rows and
  // SWIM_SENIOR_200 (210s × 2-heat) proportionally rounded up
  // for the 200y rows — same defaults the Distance dropdown will
  // recompute to if a coach later scales one of these rows.
  ageGroups.forEach((ag) => {
    const isImYoung = ag.label !== '13-14' && ag.label !== '15-18';
    out.push({
      name: `${isImYoung ? 100 : 200}${u} IM`,
      type: 'distance',
      heats: isImYoung ? 3 : 2,
      durationSeconds: isImYoung ? 180 : 240,
      ageGroup: ag.label,
    });
  });

  // 7. Long Freestyle — 50 young / 100 older.
  ageGroups.forEach((ag) => {
    out.push({
      name: `${ag.young ? 50 : 100}${u} Freestyle`,
      type: ag.young ? 'sprint' : 'distance',
      heats: 2,
      durationSeconds: ag.young ? 110 : 180,
      ageGroup: ag.label,
    });
  });

  // 8. Freestyle Relays — 100 young / 200 older, closing each section.
  ageGroups.forEach((ag) => {
    out.push({
      name: `${ag.young ? 100 : 200}${u} Freestyle Relay`,
      type: 'relay',
      heats: 1,
      durationSeconds: ag.young ? 150 : 240,
      ageGroup: ag.label,
    });
  });

  return out;
}

// ---------------------------------------------------------------------------
// XC presets — flat per-level, no preset switcher.
// ---------------------------------------------------------------------------
const XC_EVENTS_BY_LEVEL = {
  'high-school': [
    { name: 'Freshman Race', type: 'distance', heats: 1, durationSeconds: 1500, ageGroup: 'Freshman' },
    { name: 'JV Race', type: 'distance', heats: 1, durationSeconds: 1500, ageGroup: 'JV' },
    { name: 'Varsity Race', type: 'distance', heats: 1, durationSeconds: 1500, ageGroup: 'Varsity' },
  ],
  'middle-school': [
    { name: '7th Grade Race', type: 'distance', heats: 1, durationSeconds: 900, ageGroup: '7th Grade' },
    { name: '8th Grade Race', type: 'distance', heats: 1, durationSeconds: 900, ageGroup: '8th Grade' },
    { name: 'Open Race', type: 'distance', heats: 1, durationSeconds: 1200, ageGroup: 'Open' },
  ],
  'college': [
    { name: 'JV Race', type: 'distance', heats: 1, durationSeconds: 1800, ageGroup: 'JV' },
    { name: 'Varsity Race', type: 'distance', heats: 1, durationSeconds: 1800, ageGroup: 'Varsity' },
  ],
};

function normalizeLevel(level) {
  if (!level) return 'high-school';
  return String(level).replace('professional', 'college');
}

/**
 * Return the default event list to pre-populate the wizard's
 * ManualEventEntry for a non-track sport. For track, callers should keep
 * using `getEventsForMeet` from eventConfig.js.
 *
 * `options.swimPreset` selects between 'standard_hs' (default) and
 * 'summer_club' when sport is swim. Ignored for xc.
 * `options.poolUnit` controls the unit suffix on generated swim event
 * names ('y' for yards, 'm' for meters) and swaps the NFHS 500 free
 * for the 400 free in metric pools. Ignored for xc.
 */
export function getDefaultEventsForSport(sportType, level, options = {}) {
  const sport = normalizeSportType(sportType);
  if (sport === 'track') return null; // caller must use track-specific config
  const normLevel = normalizeLevel(level);

  let rawEvents;
  if (sport === 'swim') {
    const preset = normalizeSwimPreset(options.swimPreset);
    const unit = normalizePoolUnit(options.poolUnit);
    if (preset === 'summer_club') {
      rawEvents = buildSummerClubEvents(unit);
    } else {
      // standard_hs bucket varies by school level so middle-school dual
      // meets get the short-course 50-distance list, not 100s.
      if (normLevel === 'middle-school') {
        rawEvents = buildMiddleSchoolSwimEvents(unit);
      } else if (normLevel === 'college') {
        rawEvents = buildCollegeSwimEvents(unit);
      } else {
        rawEvents = buildStandardHSSwimEvents(unit);
      }
    }
  } else {
    rawEvents = XC_EVENTS_BY_LEVEL[normLevel] || XC_EVENTS_BY_LEVEL['high-school'];
  }

  return rawEvents.map((e) => buildTemplate(sport, normLevel, e));
}

// Spelling helpers — capitalize/uncapitalize the vocab once for callers
// that want a consistent inline string without manually casing things.
export function pluralEvent(sportType) {
  return getSportVocab(sportType).events;
}
export function pluralHeat(sportType) {
  return getSportVocab(sportType).heats;
}
